import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Organization, OrganizationMember, Role, User } from '../types';
import { RegisterPayload, restoreSession, Session, signIn as signInService, signOut as signOutService, signUp as signUpService } from '../services/authService';
import { loadPreferences } from '../services/preferencesService';
import { ensureDefaultContent } from '../services/repair';
import { refresh as refreshStore } from '../services/store';
import { can, Permission } from '../config/permissions';
import { useDatabase } from '../hooks/useDatabase';
interface AuthContextValue {
  session: Session | null;
  user: User | null;
  organization: Organization | null;
  members: OrganizationMember[];
  role: Role | null;
  loading: boolean;
  /** true mientras se recupera la sesión guardada por Supabase. */
  restoring: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Devuelve null si la cuenta necesita confirmarse por email. */
  register: (payload: RegisterPayload) => Promise<Session | null>;
  logout: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Completa el contenido predeterminado del espacio de trabajo (formulario de
 * siete pasos y landings con y sin vídeo) en cada acceso. Es idempotente:
 * solo crea lo que falte, así que también repara organizaciones antiguas.
 */
async function repairWorkspace(session: Session): Promise<void> {
  try {
    const result = await ensureDefaultContent(session.organizationId, session.organizationName ?? 'Mi empresa');
    if (result.created || result.addedQuestions > 0) await refreshStore();
  } catch (err) {
    // No debe impedir el acceso: se registra para poder diagnosticarlo.
    console.error('[AVA] No se ha podido completar el contenido inicial:', err);
  }
}
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const db = useDatabase();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(true);

  // Supabase guarda la sesión: al recargar se recupera y se rehidrata todo.
  useEffect(() => {
    let active = true;
    restoreSession().then(async next => {
      if (!active || !next) return;
      setSession(next);
      await loadPreferences(next.user.id, next.organizationId);
      await repairWorkspace(next);
    }).finally(() => {
      if (active) setRestoring(false);
    });
    return () => {
      active = false;
    };
  }, []);
  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const next = await signInService(email, password);
      setSession(next);
      await loadPreferences(next.user.id, next.organizationId);
      await repairWorkspace(next);
    } finally {
      setLoading(false);
    }
  }, []);
  const register = useCallback(async (payload: RegisterPayload) => {
    setLoading(true);
    try {
      const next = await signUpService(payload);
      if (next) {
        setSession(next);
        await loadPreferences(next.user.id, next.organizationId);
      }
      return next;
    } finally {
      setLoading(false);
    }
  }, []);
  const logout = useCallback(async () => {
    await signOutService();
    setSession(null);
  }, []);
  const organization = useMemo(() => session ? db.organizations.find(o => o.id === session.organizationId) ?? null : null, [db.organizations, session]);
  const members = useMemo(() => session ? db.members.filter(m => m.organizationId === session.organizationId) : [], [db.members, session]);
  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    organization,
    members,
    role: session?.role ?? null,
    loading: loading || restoring,
    restoring,
    login,
    register,
    logout,
    hasPermission: permission => session ? can(session.role, permission) : false
  };
  return <AuthContext.Provider value={value} data-id="element-722" data-mp="2i2x76p3mssje">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}

/** Datos del espacio de trabajo activo, ya filtrados por organización. */
export function useWorkspace() {
  const {
    session
  } = useAuth();
  const db = useDatabase();
  const organizationId = session?.organizationId ?? '';
  return useMemo(() => ({
    organizationId,
    // Los elementos con borrado lógico quedan fuera de las vistas normales.
    leads: db.leads.filter(l => l.organizationId === organizationId && !l.deletedAt),
    trashedLeads: db.leads.filter(l => l.organizationId === organizationId && l.deletedAt),
    forms: db.forms.filter(f => f.organizationId === organizationId && !f.deletedAt),
    trashedForms: db.forms.filter(f => f.organizationId === organizationId && f.deletedAt),
    landings: db.landings.filter(l => l.organizationId === organizationId && !l.deletedAt),
    trashedLandings: db.landings.filter(l => l.organizationId === organizationId && l.deletedAt),
    stages: db.stages.filter(s => s.organizationId === organizationId).sort((a, b) => a.order - b.order),
    sources: db.sources.filter(s => s.organizationId === organizationId).sort((a, b) => a.order - b.order),
    notes: db.notes.filter(n => n.organizationId === organizationId && !n.deletedAt),
    history: db.history.filter(h => h.organizationId === organizationId),
    invitations: db.invitations.filter(i => i.organizationId === organizationId),
    components: db.components.filter(c => c.organizationId === organizationId),
    files: db.files.filter(f => f.organizationId === organizationId),
    isDemo: db.demo
  }), [db, organizationId]);
}